// Homepage with animated flag and hero section

import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Users, 
  MapPin, 
  Calendar, 
  FileText, 
  Award
} from 'lucide-react';
import Button from '../components/ui/Button';
import { useDashboardStats } from '../hooks/useDashboardStats';

const HomePage: React.FC = () => {
  const { stats, isLoading } = useDashboardStats();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };


  const statsData = [
    {
      icon: MapPin,
      label: 'Active Chapters',
      value: stats?.chapters || 0,
      color: 'text-primary-600',
    },
    {
      icon: Users,
      label: 'Members',
      value: stats?.members || 0,
      color: 'text-secondary-600',
    },
    {
      icon: Calendar,
      label: 'Upcoming Events',
      value: stats?.upcoming_events || 0,
      color: 'text-accent-600',
    },
    {
      icon: FileText,
      label: 'Published Reports',
      value: stats?.published_reports || 0,
      color: 'text-purple-600',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section with Flag Background */}
      <section className="relative overflow-hidden min-h-screen w-full flex items-center">
        {/* Full Flag Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="/user-flag-image.jpg"
            alt="Indian Flag Background"
            className="w-full h-full object-cover object-center"
            style={{
              minHeight: '100vh',
              minWidth: '100vw'
            }}
          />
          {/* Overlay for text readability */}
          <div className="absolute inset-0 bg-white/75 backdrop-blur-[2px]"></div>
        </div>

        <div className="container-width section-padding relative w-full">
          <motion.div
            className="py-12 md:py-20"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Centered Content */}
            <div className="max-w-4xl mx-auto text-center relative z-10">
              <motion.div variants={itemVariants}>
                <motion.div
                  className="inline-flex items-center space-x-2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-medium text-primary-700 border border-primary-200 mb-6"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <Award className="w-4 h-4" />
                  <span>Building Transparent Democracy</span>
                </motion.div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                  <span className="text-gradient-flag">Transparency.</span>
                  <br />
                  <span className="text-gradient-flag">Accountability.</span>
                  <br />
                  <span className="text-gradient-flag">Awareness.</span>
                </h1>

                <p className="text-lg md:text-xl lg:text-2xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
                  Join the movement to create transparent institutions and accountable governance. 
                  Together, we can build a democracy that serves every citizen with integrity and openness.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                  <Link to="/chapters">
                    <Button size="lg" className="group backdrop-blur-sm bg-primary-600/90 hover:bg-primary-700 shadow-lg">
                      Find Your Local Chapter
                      <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button variant="outline" size="lg" className="backdrop-blur-sm bg-white/80 hover:bg-white/90 border-2">
                      Start a New Chapter
                    </Button>
                  </Link>
                </div>

                {/* Stats - Centered */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl mx-auto">
                  {statsData.map((stat, index) => {
                    const Icon = stat.icon;
                    return (
                      <motion.div
                        key={stat.label}
                        className="text-center bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/50"
                        variants={itemVariants}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.9)' }}
                      >
                        <div className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${stat.color} bg-current/10 mb-2`}>
                          <Icon className={`w-4 h-4 ${stat.color}`} />
                        </div>
                        <div className={`text-2xl font-bold ${stat.color}`}>
                          {isLoading ? '...' : stat.value.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600">{stat.label}</div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
